import * as React from "react";
import { AsyncLoadResult, AsyncLoadOptions } from "../../shared/abstractStore";
import { useAsyncCallback } from "react-use-async-callback";
import { useAccountService } from "./useAccountService";
import { useAuthenticatedState } from "../../api/api-authorization/useAuthenticatedState";
import { isNullOrUndefined } from "util";
import { AccountService } from "./AccountService";
import { CurrentUserRolesCache, useCurrentUserRolesCache } from "../../globalState/currentUserDetails/useCurrentUserRolesCache";


/**
 * Refresh the current user state using the accountService.
 * @param isAuthenticated
 */
const refreshCurrentUserRoles = async (isAuthenticated: boolean | null, accountService: AccountService, setRoles: (value: React.SetStateAction<CurrentUserRolesCache>) => void): Promise<void> => {
    // Do nothing if we don't know our authentication state.
    if (isNullOrUndefined(isAuthenticated)) {
        return;
    }

    // If we are not authenticated then we have no roles.
    if (!isAuthenticated) {
        setRoles({ isAuthenticated: false, roles: [], isLoading: false });
        return;
    }

    // Otherwise we are authenticated, so load our roles.
    setRoles(prevState => ({ ...prevState, isLoading: true }));
    let result = await accountService.currentUserRoles();
    setRoles({ isAuthenticated: true, roles: result.roles, isLoading: false });
};

/**
 * Returns the list of roles for the current user.
 */
export function useCurrentUserRoles(options: AsyncLoadOptions = {}): AsyncLoadResult<{ roles: Array<string> | null }> {
    const { isAuthenticated } = useAuthenticatedState();
    const [roles, setRoles] = useCurrentUserRolesCache();
    const accountService = useAccountService();

    const [refresh, { isExecuting: isLoading, errors }] = useAsyncCallback(async () => {
        // Don't trigger more than one attempt at loading the roles at once.
        if (roles.isLoading) {
            return;
        }

        await refreshCurrentUserRoles(isAuthenticated, accountService, setRoles);
    }, [accountService, isAuthenticated, setRoles, roles]);

    React.useEffect(() => {
        if (options.lazy) {
            return;
        }

        // Refresh from the store if we need to.
        if (isNullOrUndefined(isAuthenticated)) {
            return;
        }

        if (!roles || roles.isAuthenticated !== isAuthenticated) {
            refresh();
        }
    }, [refresh, options.lazy, isAuthenticated, roles]);

    // TODO handle options.pollInterval.

    return {
        data: { roles: roles?.roles ?? null },
        refresh: refresh,
        isLoading: isLoading,
        errors: errors
    };
}


/**
 * Clear the cached user roles.
 */
export function useResetCurrentUserRolesCallback() {
    const { isAuthenticated } = useAuthenticatedState();
    const [/*roles*/, setRoles] = useCurrentUserRolesCache();
    const accountService = useAccountService();

    const [refresh] = useAsyncCallback(async () => {
        await refreshCurrentUserRoles(isAuthenticated, accountService, setRoles);
    }, [accountService, isAuthenticated]);

    return React.useCallback(() => {
        refresh();
    }, [refresh]);
}
